<template>
  <v-container grid-list-md fluid class="pa-2">
    <v-layout row wrap>
      <v-container grid-list-md fluid class="pa-1">
        <v-layout row wrap>
          <v-flex xs12 sm6>
            <v-card class="ticket max-shadow">
              <v-card-title>
                <div class="title-2">Tickets</div>
                <v-spacer />
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn depressed v-on="on">
                      {{ category.title }}
                      <v-icon class="pl-2">arrow_drop_down</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-tile
                      v-for="id in Object.keys(categoryList)"
                      :key="id"
                      @click="updateCategory(id, categoryList[id])"
                    >
                      <v-list-tile-title>{{ categoryList[id].title }}</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
                <ticket-filter v-model="filters" @change="filterTickets" />
              </v-card-title>
              <v-divider />
              <v-card-text class="px-1">
                <v-layout v-if="tickets.length === 0" row wrap>
                  <span style="margin: 0 auto" class="grey--text">There are no tickets to show</span>
                </v-layout>
                <v-subheader v-if="openTickets.length > 0">Open</v-subheader>
                <div v-for="ticket in openTickets" :key="ticket.id">
                  <v-flex xs12>
                    <ticket-tile
                      v-ripple
                      style="cursor: pointer"
                      :class="`active_${ticket.id}`"
                      :ticket="ticket"
                      @click="openTicket(ticket)"
                    />
                  </v-flex>
                </div>
                <v-subheader v-if="closedTickets.length > 0">Closed</v-subheader>
                <div v-for="ticket in closedTickets" :key="ticket.id">
                  <v-flex xs12>
                    <ticket-tile
                      v-ripple
                      style="cursor: pointer"
                      :class="`active_${ticket.id}`"
                      :ticket="ticket"
                      @click="openTicket(ticket)"
                    />
                  </v-flex>
                </div>
                <v-layout row wrap justify-center>
                  <v-btn
                    v-if="tickets.length !== 0 && tickets.length % 10 === 0"
                    color="primary"
                    flat
                    center
                    :loading="loadMoreLoading"
                    @click="loadMoreTickets"
                  >load more</v-btn>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-flex>
          <v-flex xs12 sm6>
            <ticket :ticket="selectedTicket" />
          </v-flex>
        </v-layout>
      </v-container>
    </v-layout>
    <v-snackbar
      v-model="snackbar"
      :right="true"
      :timeout="4000"
      :top="true"
      :color="snackColor"
    >{{ snackText }}</v-snackbar>
  </v-container>
</template>

<script>
import { firestore } from '@/firebase';
import * as moment from 'moment';
import TicketTile from '@/components/common/TicketTile';
import Ticket from './Ticket';
import _get from 'lodash/get';

import TicketFilter from './TicketFilter';

export default {
  name: 'HelpDesk',
  components: {
    ticket: Ticket,
    'ticket-tile': TicketTile,
    'ticket-filter': TicketFilter
  },
  data() {
    return {
      tickets: [],
      selectedTicket: {},
      commentText: '',
      loadMoreLoading: false,
      commentLoading: false,
      filteredQuery: [],
      limit: 10,
      filters: ['OPEN', 'CLOSED', 'REOPENED'],
      dateFormat: 'DD MMM YYYY',
      lastSelectedTicketId: '',
      snackbar: false,
      snackColor: 'success',
      snackText: '',
      category: { title: 'Select Category', id: 'all' },
      categoryList: {}
    };
  },
  computed: {
    status() {
      return this.selectedTicket.closed ? 'Closed' : 'Open';
    },
    changeStatusBtnText() {
      return this.selectedTicket.closed ? 'Reopen' : 'close';
    },
    createdAt() {
      const date = _get(this.selectedTicket, 'createdAt');
      if (date !== undefined)
        return moment(date.toDate()).format(this.dateFormat);
      return '';
    },
    role() {
      return _get(this.$store.state.profile, 'role');
    },
    cid() {
      return _get(this.$store.state.profile, 'cid');
    },
    openTickets() {
      return this.tickets.filter(ticket => ticket.status !== 'closed');
    },
    closedTickets() {
      return this.tickets.filter(ticket => ticket.status === 'closed');
    },
    ticketsRef() {
      return this.role === 'admin'
        ? firestore
            .doc(`helpDesk/${this.cid}`)
            .collection('tickets')
            .orderBy('createdAt', 'desc')
        : firestore
            .doc('helpDesk/buildingSupport')
            .collection('tickets')
            .orderBy('createdAt', 'desc');
    }
  },
  watch: {
    tickets: {
      handler(val) {
        if (val.length < 0) return (this.selectedTicket = {});
        const openTickets = val.filter(ticket => ticket.status !== 'closed');
        const closedTickets = val.filter(ticket => ticket.status === 'closed');
        if (openTickets.length !== 0) {
          this.selectedTicket = openTickets[0];
          this.$nextTick().then(() => {
            const list = document.getElementsByClassName(
              `active_${this.selectedTicket.id}`
            )[0];
            list.classList.add('blue', 'lighten-5');
            this.lastSelectedTicketId = openTickets[0].id;
          });
        } else if (closedTickets.length !== 0) {
          this.selectedTicket = closedTickets[0];
          this.$nextTick().then(() => {
            const list = document.getElementsByClassName(
              `active_${this.selectedTicket.id}`
            )[0];
            list.classList.add('blue', 'lighten-5');
            this.lastSelectedTicketId = closedTickets[0].id;
          });
        }
      },
      immediate: true,
      deep: true
    }
  },
  created() {
    this.$store.commit('setToolbarTitle', { toolbarTitle: 'Help Desk' });
  },
  mounted() {
    this.ticketsRef
      .limit(this.limit)
      .get()
      .then(tickets => {
        const docs = [];
        tickets.forEach(docSnapshot => {
          docs.push({ ...docSnapshot.data(), ...{ id: docSnapshot.id } });
        });
        this.tickets = docs;
      });

    if (this.role == 'admin') {
      firestore
        .doc(`helpDesk/${this.cid}`)
        .get()
        .then(snapshot => {
          const docData = (snapshot.data() || {}).categories || {};

          this.categoryList = docData;
          this.categoryList.all = { title: 'All', id: 'all' };
        });
    } else if (this.role == 'max-admin') {
      firestore
        .doc(`helpDesk/buildingSupport`)
        .get()
        .then(snapshot => {
          const docData = (snapshot.data() || {}).categories || {};

          this.categoryList = docData;
          this.categoryList.all = { title: 'All', id: 'all' };
        });
    }
  },
  methods: {
    openTicket(ticket) {
      this.selectedTicket = ticket;
      if (this.lastSelectedTicketId) {
        document
          .getElementsByClassName(`active_${this.lastSelectedTicketId}`)[0]
          .classList.remove('blue', 'lighten-5');
        const list = document.getElementsByClassName(`active_${ticket.id}`)[0];
        list.classList.add('blue', 'lighten-5');
        this.lastSelectedTicketId = ticket.id;
      }
    },
    formatDate(timestamp) {
      const date = timestamp.toDate();
      return moment(date).format(this.dateFormat);
    },

    _toArray(obj) {
      if (!obj) return [];
      return Object.keys(obj).map(key => {
        const item = obj[key];
        item.id = key;
        return item;
      });
    },

    loadMoreTickets() {
      this.limit = this.limit * 2;
      this.loadMoreLoading = true;
      let queries = [];
      if (this.filteredQuery.length === 0) {
        queries.push(this.ticketsRef);
      } else {
        queries = this.filteredQuery;
      }
      this.getData(queries);
    },
    filterTickets(filters) {
      this.filteredQuery = [];
      let queries = [];
      const getAll = filters.length > 2;
      if (getAll) {
        queries.push(this.ticketsRef);
      } else {
        filters.forEach(filter => {
          queries.push(this.ticketsRef.where('status', '==', filter));
        });
      }
      this.filteredQuery = queries;
      this.getData(queries);
    },

    getData(queries) {
      const queryPromises = [];
      queries.forEach(query => {
        const queryPromise = (this.category.id === 'all'
          ? query
          : query.where('categoryId', '==', this.category.id)
        )
          .limit(this.limit)
          .get()
          .then(tickets => {
            const docs = [];
            tickets.forEach(docSnapshot => {
              docs.push({ ...docSnapshot.data(), ...{ id: docSnapshot.id } });
            });
            return docs;
          });
        queryPromises.push(queryPromise);
      });
      Promise.all(queryPromises).then(results => {
        let combinedArray = [];
        results.forEach(result => {
          combinedArray = combinedArray.concat(result);
        });
        this.tickets = combinedArray;
        this.loadMoreLoading = false;
      });
      this.selectedTicket = {};
    },
    updateCategory(id, value) {
      if (this.category.id === id && this.category.title === value.title) return;
      value.id = id;

      this.category = value;
      this.filterTickets(this.filters);
    }
  }
};
</script>

<style scoped>
.ticket {
  height: calc(100vh - var(--toolbar-height)) !important;
  overflow-y: scroll;
}
</style>